/**
 * Specialized handlers for note LexicalTextEditor (handles both part notes and regular notes)
 */
import {
  updateTextAndSelectedText,
  updateTextWithCallback,
} from "../utils/stateUpdateUtils";

/**
 * Update partsByType for note changes on part content
 */
const updatePartsByTypeForNote = ({
  text,
  selectedContentId,
  field,
  value,
}) => {
  return Object.fromEntries(
    Object.entries(text.partsByType || {}).map(([typeName, typeData]) => {
      const updatedParts = typeData.parts.map((partObj) => {
        // Skip null or undefined partObj entries
        if (!partObj) return null;

        return partObj.id === selectedContentId
          ? { ...partObj, [field]: value }
          : partObj;
      }).filter(Boolean); // Remove any null entries
      return [typeName, { ...typeData, parts: updatedParts }];
    })
  );
};

/**
 * Create note content change handler
 */
export const createNoteContentChangeHandler = ({
  text,
  setTexts,
  setSelectedText,
  setPendingChanges,
  isElementBeingEdited,
}) => {
  return (htmlContent, plainText) => {
    const cleanedContent = htmlContent;
    const isHtml = /<\/?[a-z][\s\S]*?>/i.test(cleanedContent);

    // CRITICAL: Mark that there are pending changes so history will be saved on unselection
    if (isElementBeingEdited && typeof setPendingChanges === "function") {
      setPendingChanges(true);
    }

    if (text.selectedContent?.type === "part") {
      const selectedPartId = text.selectedContent.id;

      const updatedPartsByType = Object.fromEntries(
        Object.entries(text.partsByType || {}).map(([typeKey, typeData]) => [
          typeKey,
          {
            ...typeData,
            parts: typeData.parts.map((partObj) => {
              // Skip null or undefined partObj entries
              if (!partObj) return null;

              return partObj.id === selectedPartId
                ? {
                  ...partObj,
                  // Clear note when plainText is empty, otherwise update with new content
                  note: plainText ? cleanedContent : "",
                  notePlainText: plainText || "",
                  noteIsHtml: plainText ? isHtml : false,
                }
                : partObj;
            }).filter(Boolean),
          },
        ])
      );

      updateTextWithCallback({
        textId: text.id,
        setTexts,
        setSelectedText,
        textUpdater: (t) => ({
          ...t,
          partsByType: updatedPartsByType,
          _lastUpdated: Date.now(),
        }),
        selectedTextUpdater: (prev) => {
          const updatedPartsByType = {
            ...prev.partsByType,
          };

          Object.entries(updatedPartsByType).forEach(([typeKey, typeData]) => {
            updatedPartsByType[typeKey] = {
              ...typeData,
              parts: typeData.parts.map((partObj) => {
                // Skip null or undefined partObj entries
                if (!partObj) return null;

                return partObj.id === selectedPartId
                  ? {
                    ...partObj,
                    // Clear note when plainText is empty, otherwise update with new content
                    note: plainText ? cleanedContent : "",
                    notePlainText: plainText || "",
                    noteIsHtml: plainText ? isHtml : false,
                  }
                  : partObj;
              }).filter(Boolean),
            };
          });

          return {
            ...prev,
            partsByType: updatedPartsByType,
            _lastUpdated: Date.now(),
          };
        },
      });
    } else {
      updateTextAndSelectedText({
        textId: text.id,
        setTexts,
        setSelectedText,
        updates: {
          note: plainText ? cleanedContent : "",
          notePlainText: plainText,
          noteIsHtml: plainText ? isHtml : false,
          _lastUpdated: Date.now(),
        },
      });
    }
  };
};

/**
 * Create note color change handler
 */
export const createNoteColorChangeHandler = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
}) => {
  return (newColor) => {
    if (text.selectedContent?.type === "part") {
      const selectedContentId = text.selectedContent.id;
      const updatedPartsByType = updatePartsByTypeForNote({
        text,
        selectedContentId,
        field: "noteColor",
        value: newColor,
      });

      setTexts((prevTexts) =>
        prevTexts.map((t) =>
          t.id === text.id ? { ...t, partsByType: updatedPartsByType } : t
        )
      );

      setSelectedText((prev) => {
        if (!prev) return prev;
        const updatedPartsByType = updatePartsByTypeForNote({
          text,
          selectedContentId,
          field: "noteColor",
          value: newColor,
        });
        return { ...prev, partsByType: updatedPartsByType };
      });
    } else {
      updateTextAndSelectedText({
        textId: text.id,
        setTexts,
        setSelectedText,
        updates: {
          noteColor: newColor,
          _lastUpdated: Date.now(),
        },
      });
    }
    saveToHistory();
  };
};

/**
 * Create note underline change handler
 */
export const createNoteUnderlineChangeHandler = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
}) => {
  return () => {
    if (text.selectedContent?.type === "part") {
      const selectedContentId = text.selectedContent.id;

      setTexts((prev) =>
        prev.map((t) =>
          t.id !== text.id
            ? t
            : {
              ...t,
              partsByType: Object.fromEntries(
                Object.entries(t.partsByType || {}).map(([type, data]) => [
                  type,
                  {
                    ...data,
                    parts: data.parts.map((p) =>
                      p.id === selectedContentId
                        ? { ...p, noteUnderline: !p.noteUnderline }
                        : p
                    ),
                  },
                ])
              ),
            }
        )
      );

      setSelectedText((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          partsByType: Object.fromEntries(
            Object.entries(prev.partsByType || {}).map(([type, data]) => [
              type,
              {
                ...data,
                parts: data.parts.map((p) =>
                  p.id === selectedContentId
                    ? { ...p, noteUnderline: !p.noteUnderline }
                    : p
                ),
              },
            ])
          ),
        };
      });
    } else {
      setTexts((prev) =>
        prev.map((t) =>
          t.id === text.id
            ? {
              ...t,
              noteUnderline: !t.noteUnderline,
              _lastUpdated: Date.now(),
            }
            : t
        )
      );
      setSelectedText((prev) =>
        prev
          ? {
            ...prev,
            noteUnderline: !prev.noteUnderline,
            _lastUpdated: Date.now(),
          }
          : prev
      );
    }
    saveToHistory();
  };
};

/**
 * Create note highlight change handler
 */
export const createNoteHighlightChangeHandler = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
}) => {
  return () => {
    if (text.selectedContent?.type === "part") {
      const selectedContentId = text.selectedContent.id;

      setTexts((prev) =>
        prev.map((t) =>
          t.id !== text.id
            ? t
            : {
              ...t,
              partsByType: Object.fromEntries(
                Object.entries(t.partsByType || {}).map(([type, data]) => [
                  type,
                  {
                    ...data,
                    parts: data.parts.map((p) =>
                      p.id === selectedContentId
                        ? { ...p, noteHighlighted: !p.noteHighlighted }
                        : p
                    ),
                  },
                ])
              ),
            }
        )
      );

      setSelectedText((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          partsByType: Object.fromEntries(
            Object.entries(prev.partsByType || {}).map(([type, data]) => [
              type,
              {
                ...data,
                parts: data.parts.map((p) =>
                  p.id === selectedContentId
                    ? { ...p, noteHighlighted: !p.noteHighlighted }
                    : p
                ),
              },
            ])
          ),
        };
      });
    } else {
      setTexts((prev) =>
        prev.map((t) =>
          t.id === text.id
            ? {
              ...t,
              noteHighlighted: !t.noteHighlighted,
              _lastUpdated: Date.now(),
            }
            : t
        )
      );
      setSelectedText((prev) =>
        prev
          ? {
            ...prev,
            noteHighlighted: !prev.noteHighlighted,
            _lastUpdated: Date.now(),
          }
          : prev
      );
    }
    saveToHistory();
  };
};

/**
 * Create note link change handler
 */
export const createNoteLinkChangeHandler = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
}) => {
  return () => {
    if (text.selectedContent?.type === "part") {
      const selectedContentId = text.selectedContent.id;
      const updatedPartsByType = updatePartsByTypeForNote({
        text,
        selectedContentId,
        field: "noteLink",
        value: true,
      });

      setTexts((prevTexts) =>
        prevTexts.map((t) =>
          t.id === text.id ? { ...t, partsByType: updatedPartsByType } : t
        )
      );

      setSelectedText((prev) => {
        if (!prev) return prev;
        const updatedPartsByType = updatePartsByTypeForNote({
          text,
          selectedContentId,
          field: "noteLink",
          value: true,
        });
        return { ...prev, partsByType: updatedPartsByType };
      });
    } else {
      updateTextAndSelectedText({
        textId: text.id,
        setTexts,
        setSelectedText,
        updates: {
          noteLink: true,
          _lastUpdated: Date.now(),
        },
      });
    }
    saveToHistory();
  };
};

/**
 * Get note content based on context (part vs regular)
 */
export const getNoteContent = (text) => {
  if (text?.selectedContent?.type === "part" && text?.selectedContent?.id) {
    const selectedPartId = text.selectedContent.id;
    const selectedPart = Object.entries(text.partsByType || {})
      .flatMap(([_, typeData]) => typeData.parts || [])
      .find((partObj) => partObj?.id === selectedPartId);

    // Return the note if it exists (could be empty string, but that's valid)
    const note = selectedPart?.note;
    return note !== undefined && note !== null ? note : "";
  } else {
    return text?.note || "";
  }
};

/**
 * Check if note should save history on blur
 */
export const shouldSaveNoteHistory = (text) => {
  return !(text?.selectedContent?.type === "part" && text?.selectedContent?.id);
};
